import { useState } from 'react'
import { createTodo } from '@core/letters'
import { parsePhrase } from '@core/phrase'
import type { FamilySession } from '@core/session'

/**
 * Adding something in one line.
 *
 * This is the whole difference between a household list that gets used and one
 * that gets abandoned. "Melk kopen" cost a full-screen form with four fields
 * and two taps before this; nobody does that standing in a kitchen, so they
 * write it on a scrap of paper instead and the app quietly stops being the
 * place where the list lives.
 *
 * So: type, press enter, it is in — and the field stays focused because chores
 * arrive in threes. Everything the form offers (a date, whose it is, what
 * exactly they should do) is still there behind "Meer", for the one task in ten
 * that needs it.
 *
 * The optimism is deliberate. The row appears before the database has
 * answered, the same reasoning as rule 21 for capture: the person has moved on
 * already, and a spinner on a to-do is a spinner on nothing.
 *
 * "Vuilnis dinsdag" gets its date read out of it, by the same deterministic
 * parser the payment line uses. Only the date — an amount in a chore is part of
 * its name ("2 broden"), never a field.
 */
export function QuickAdd({
  session,
  onAdded,
  onMore,
}: {
  session: FamilySession
  onAdded: () => void
  onMore: () => void
}) {
  const [title, setTitle] = useState('')
  const [error, setError] = useState('')

  async function add() {
    const text = title.trim()
    if (!text) return
    setTitle('')
    setError('')
    const said = parsePhrase(text)
    try {
      await createTodo({
        // The first space is the household one, and a chore is a household
        // thing. Anything that belongs to the restaurant is worth the form.
        spaceId: session.spaces[0]?.id ?? '',
        // The words minus the date, unless taking the date out left nothing —
        // "morgen" on its own is a to-do called "morgen", not an empty one.
        title: said.date && said.description ? said.description : text,
        detail: null,
        dueDate: said.date,
        assignedTo: null,
        assignmentNote: null,
      })
      onAdded()
    } catch (err) {
      // Put the words back rather than losing them — retyping something you
      // already typed is the fastest way to stop trusting a box.
      setTitle(text)
      setError(err instanceof Error ? err.message : 'Toevoegen lukte niet.')
    }
  }

  return (
    <div className="shrink-0 px-4 pb-3 pt-2 border-t border-line bg-canvas">
      {error && <p className="text-danger text-sm mb-2">{error}</p>}
      <div className="flex gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add()
          }}
          placeholder="Iets toevoegen…"
          aria-label="Nieuwe taak"
          enterKeyHint="done"
          className="flex-1 min-w-0 min-h-[3rem] px-3 rounded-xl border border-line bg-transparent text-base"
        />
        <button
          onClick={() => void add()}
          disabled={!title.trim()}
          aria-label="Toevoegen"
          className="shrink-0 w-12 min-h-[3rem] rounded-xl border border-ink bg-ink/5 text-xl font-medium disabled:opacity-30"
        >
          +
        </button>
      </div>
      <button onClick={onMore} className="w-full pt-2 text-sm text-muted">
        Meer: datum, aan wie
      </button>
    </div>
  )
}
